/**
 * Pure helpers for the "嵌套字符串自动去转义" affordance on
 * `JsonTool`. Tests live in `tests/web/jsonUnescapeTool.test.ts`.
 *
 * Two related but distinct operations live here:
 *
 *   1. {@link detectEscapedJson} — given user input, decide whether
 *      it's a JSON value whose payload is *itself* a JSON string
 *      (`"{\"a\":1}"` or `"{\\\"a\\\":1}"` etc.). Used to render the
 *      "看起来是字符串嵌字符串，要解开吗？" button on the page.
 *
 *   2. {@link unescapeJsonString} — given a JSON string literal,
 *      produce the inner string. Used for both "解开嵌套" and the
 *      "把代码里的转义 JSON 还原" workflow.
 */

export interface EscapeDetection {
  /** True when input parses to a string and that string itself parses to a JSON value. */
  isNested: boolean;
  /** Inner value once unwrapped, or undefined when `isNested` is false. */
  inner?: unknown;
  /** Pretty-printed inner JSON (2-space) when `isNested` is true. Convenience for the UI. */
  innerPretty?: string;
}

/**
 * Detect a "double-encoded" JSON payload — one where the outer JSON
 * value is a string whose contents is itself JSON.
 *
 * Conservative on purpose: a string that *could* be JSON (e.g.
 * `"hello"`, parses to `"hello"`, which `JSON.parse` rejects) is NOT
 * flagged. We only fire the affordance when both passes succeed AND
 * the inner result is non-trivial (object / array / number-like).
 */
export function detectEscapedJson(raw: string): EscapeDetection {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { isNested: false };

  let outer: unknown;
  try {
    outer = JSON.parse(trimmed);
  } catch {
    return { isNested: false };
  }

  if (typeof outer !== 'string') return { isNested: false };
  // Empty string "" or whitespace-only string never produces useful inner JSON.
  if (outer.trim().length === 0) return { isNested: false };

  let inner: unknown;
  try {
    inner = JSON.parse(outer);
  } catch {
    return { isNested: false };
  }

  // Only consider it "nested JSON" when the inner is structured.
  // `JSON.parse('"42"')` would return the number 42 here; that's
  // technically nested but not interesting to the user. Restrict to
  // object / array which is what 99% of the real-world cases (Kafka
  // payloads, log lines, double-stringified RPC bodies) look like.
  if (inner === null || (typeof inner !== 'object')) {
    return { isNested: false };
  }

  return {
    isNested: true,
    inner,
    innerPretty: JSON.stringify(inner, null, 2),
  };
}

/**
 * Unwrap a JSON string literal. Equivalent to
 * `JSON.parse(input)` when `input` parses to a string; otherwise
 * returns `null`. Kept as a thin wrapper so callers don't reach for
 * `JSON.parse` directly and accidentally swallow non-string outputs.
 */
export function unescapeJsonString(raw: string): string | null {
  try {
    const v = JSON.parse(raw);
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Inverse of `unescapeJsonString` — wrap a string for embedding into
 * source code as a JSON literal. Equivalent to `JSON.stringify(raw)`
 * but kept here so the JsonTool's "复制为转义字符串" button has a
 * named operation.
 */
export function escapeAsJsonString(raw: string): string {
  return JSON.stringify(raw);
}
